<template>
  <div>
    <slot name="activator" :dialog="{ dialogVisibility: openDialog }" />
    <AppDialogContent
      v-model="dialog"
      :error="formError"
      :persistent="loading"
      :loading="loading || initLoading"
      data-test="sanctionDialog"
      @submit-form="handleSubmit"
    >
      <template #header>
        {{ $t("sanctions:SanctionForm.title") }}
      </template>

      <AppLoadingPage v-if="initLoading" />
      <section v-else>
        <v-form :disabled="loading">
          <v-radio-group
            v-model="form.sanctionedEntity"
            :disabled="
              sanctionEntityPickerDisabled || fieldDisabled('sanctionedEntity')
            "
            data-test="sanctionedEntity"
          >
            <v-radio
              :label="
                $t(
                  'sanctions:SanctionForm.formField.sanctionedEntity.choice.Person.label',
                  'Person'
                )
              "
              value="Person"
            />
            <v-radio
              :label="
                $t(
                  'sanctions:SanctionForm.formField.sanctionedEntity.choice.Horse.label',
                  'Horse'
                )
              "
              value="Horse"
            />
          </v-radio-group>
          <br />
          <AppSelectPerson
            v-if="v$.form.sanctionedPersonId"
            v-model="form.sanctionedPersonId"
            :input-props="{
              label: $t(
                'sanctions:SanctionForm.formField.sanctionedPerson.label',
                'Person'
              ),
              errorMessages: fieldErrorMessages('sanctionedPersonId'),
              disabled:
                Boolean(values.sanctionedPersonId) ||
                fieldDisabled('sanctionedPersonId')
            }"
            :query-variables="
              personHorseSearchVariables
                ? personHorseSearchVariables
                : undefined
            "
            data-test="sanctionedPersonId"
            @blur="v$.form.sanctionedPersonId.$touch()"
          />

          <AppSelectHorse
            v-if="v$.form.sanctionedHorseId"
            v-model="form.sanctionedHorseId"
            :input-props="{
              label: $t(
                'sanctions:SanctionForm.formField.sanctionedHorse.label',
                'Horse'
              ),
              errorMessages: fieldErrorMessages('sanctionedHorseId'),
              disabled:
                Boolean(values.sanctionedHorseId) ||
                fieldDisabled('sanctionedHorseId')
            }"
            class-list="mb-6"
            :query-variables="
              personHorseSearchVariables
                ? personHorseSearchVariables
                : undefined
            "
            data-test="sanctionedHorseId"
            @blur="v$.form.sanctionedHorseId.$touch()"
          />

          <v-select
            v-if="v$.form.sanctionDefinitionId"
            v-model="form.sanctionDefinitionId"
            :items="
              formFields['sanctionDefinitionId'] &&
              formFields['sanctionDefinitionId'].possibleValues
            "
            :label="
              $t(
                'sanctions:SanctionForm.formField.sanctionDefinition.label',
                'Sanction'
              )
            "
            variant="outlined"
            data-test="sanctionDefinitionId"
            :menu-props="{
              'data-test': 'sanctionDefinitionId'
            }"
            :error-messages="fieldErrorMessages('sanctionDefinitionId')"
            :disabled="fieldDisabled('sanctionDefinitionId')"
            @blur="v$.form.sanctionDefinitionId.$touch()"
          />

          <v-select
            v-if="v$.form.sanctionReasonIds"
            v-model="form.sanctionReasonIds"
            :items="
              formFields['sanctionReasonIds'] &&
              formFields['sanctionReasonIds'].possibleValues
            "
            :label="
              $t(
                'sanctions:SanctionForm.formField.sanctionReasons.label',
                'Reasons'
              )
            "
            multiple
            variant="outlined"
            data-test="sanctionReasonIds"
            :menu-props="{
              'data-test': 'sanctionReasonIds'
            }"
            :error-messages="fieldErrorMessages('sanctionReasonIds')"
            :disabled="fieldDisabled('sanctionReasonIds')"
            @blur="v$.form.sanctionReasonIds.$touch()"
          />

          <AppDatePicker
            v-if="v$.form.validFrom"
            v-model="validFrom"
            :label="
              $t(
                'sanctions:SanctionForm.formField.validFrom.label',
                'Valid from'
              )
            "
            data-test="validFrom"
            :error-messages="fieldErrorMessages('validFrom')"
            :disabled="fieldDisabled('validFrom')"
            @blur="v$.form.validFrom.$touch()"
          />

          <AppDatePicker
            v-if="v$.form.validUntil"
            v-model="validUntil"
            :label="
              $t(
                'sanctions:SanctionForm.formField.validUntil.label',
                'Valid until'
              )
            "
            data-test="validUntil"
            :error-messages="fieldErrorMessages('validUntil')"
            :disabled="fieldDisabled('validUntil')"
            @blur="v$.form.validUntil.$touch()"
          />

          <v-text-field
            v-if="v$.form.customName"
            v-model="form.customName"
            :label="
              $t('sanctions:SanctionForm.formField.customName.label', 'Name')
            "
            data-test="customName"
            variant="outlined"
            :error-messages="fieldErrorMessages('customName')"
            :disabled="fieldDisabled('customName')"
            @blur="v$.form.customName.$touch()"
          />

          <AppRichTextField
            v-if="v$.form.customPublicDescription"
            v-model="form.customPublicDescription"
            :label="
              $t(
                'sanctions:SanctionForm.formField.customPublicDescription.label',
                'Description'
              )
            "
            data-test="customPublicDescription"
            outlined
            :error-messages="fieldErrorMessages('customPublicDescription')"
            :disabled="fieldDisabled('customPublicDescription')"
            @blur="v$.form.customPublicDescription.$touch()"
          />

          <AppRichTextField
            v-if="v$.form.publicComment"
            v-model="form.publicComment"
            :label="
              $t(
                'sanctions:SanctionForm.formField.publicComment.label',
                'Public comment'
              )
            "
            :hint="
              $t(
                'sanctions:SanctionForm.formField.publicComment.hint',
                'If needed, you can add additional information that will be visible for anyone who can see the sanction'
              )
            "
            persistent-hint
            data-test="publicComment"
            outlined
            :error-messages="fieldErrorMessages('publicComment')"
            :disabled="fieldDisabled('publicComment')"
            @blur="v$.form.publicComment.$touch()"
          />

          <AppRichTextField
            v-if="v$.form.internalComment"
            v-model="form.internalComment"
            :label="
              $t(
                'sanctions:SanctionForm.formField.internalComment.label',
                'Internal comment'
              )
            "
            :hint="
              $t(
                'sanctions:SanctionForm.formField.internalComment.hint',
                'It is possible to add additional information that will be visible only for people with special rights'
              )
            "
            persistent-hint
            data-test="internalComment"
            outlined
            :error-messages="fieldErrorMessages('internalComment')"
            :disabled="fieldDisabled('internalComment')"
            @blur="v$.form.internalComment.$touch()"
          />

          <AppSelectPerson
            v-if="v$.form.relatedPersonIds"
            v-model="form.relatedPersonIds"
            :input-props="{
              multiple: true,
              label: $t(
                'sanctions:SanctionForm.formField.relatedPeople.label',
                'Related Person'
              ),
              errorMessages: fieldErrorMessages('relatedPersonIds'),
              disabled: fieldDisabled('relatedPersonIds')
            }"
            :query-variables="
              personHorseSearchVariables
                ? personHorseSearchVariables
                : undefined
            "
            data-test="relatedPersonIds"
            @blur="v$.form.relatedPersonIds.$touch()"
          />
          <AppSelectHorse
            v-if="v$.form.relatedHorseIds"
            v-model="form.relatedHorseIds"
            :input-props="{
              multiple: true,
              label: $t(
                'sanctions:SanctionForm.formField.relatedHorses.label',
                'Related horses'
              ),
              errorMessages: fieldErrorMessages('relatedHorseIds'),
              disabled: fieldDisabled('relatedHorseIds')
            }"
            class="mb-6"
            :query-variables="
              personHorseSearchVariables
                ? personHorseSearchVariables
                : undefined
            "
            data-test="relatedHorseIds"
            @blur="v$.form.relatedHorseIds.$touch()"
          />

          <AppSelectEvent
            v-if="v$.form.relatedEventIds"
            v-model="form.relatedEventIds"
            :input-props="{
              multiple: true,
              label: $t(
                'sanctions:SanctionForm.formField.relatedEvents.label',
                'Related events'
              ),
              errorMessages: fieldErrorMessages('relatedEventIds'),
              disabled: fieldDisabled('relatedEventIds')
            }"
            server-sort-by="startDate"
            server-sort-desc
            data-test="relatedEventIds"
            @blur="v$.form.relatedEventIds.$touch()"
          />
        </v-form>
      </section>
    </AppDialogContent>
  </div>
</template>
<script>
import { ref } from "vue";
import gql from "fraql";
import { useFormValidation } from "@/composables/form/form_validation";
import { useSaveMeta } from "@/composables/form/save_meta";
import { useDateRangeBorkedPrevention } from "@/composables/form/date_range_borked_prevention";

import AppLoadingPage from "@elements/AppLoadingPage.vue";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppSelectEvent from "@AppSelects/AppSelectEvent.vue";
import AppSelectPerson from "@AppSelects/AppSelectPerson.vue";
import AppSelectHorse from "@AppSelects/AppSelectHorse.vue";
import AppDatePicker from "@elements/AppDatePicker.vue";
import AppRichTextField from "@elements/AppRichTextField.vue";

export default {
  name: "SanctionDialog",
  components: {
    AppDialogContent,
    AppLoadingPage,
    AppSelectPerson,
    AppSelectHorse,
    AppDatePicker,
    AppSelectEvent,
    AppRichTextField
  },
  props: {
    sanctionedEntity: {
      type: String,
      default: () => null,
      validate(val) {
        return ["Person", "Horse", null].includes(val);
      }
    },
    values: {
      type: Object,
      default: () => ({
        sanctionedPersonId: null,
        sanctionedHorseId: null,
        relatedEventIds: []
      })
    },
    refetchQueryName: {
      type: String,
      required: true,
      validate(val) {
        return ["person", "horse", "sanctions"].includes(val);
      }
    },
    personHorseSearchVariables: {
      type: Object,
      default: null
    }
  },
  emits: ["open-sanction-tab"],
  setup() {
    const form = ref({
      id: null,
      sanctionDefinitionId: null,
      sanctionedPersonId: null,
      sanctionedHorseId: null,
      sanctionedEntity: null,
      sanctionReasonIds: [],
      validFrom: null,
      validUntil: null,
      relatedEventIds: [],
      relatedPersonIds: [],
      relatedHorseIds: [],
      publicComment: null,
      internalComment: null
    });
    const { saveMetaSetOriginalForm, saveMetaConfirmUnsaveChanges } =
      useSaveMeta(form, { usageInModal: true });

    const { startDate: validFrom, endDate: validUntil } =
      useDateRangeBorkedPrevention(form, {
        startKey: "validFrom",
        endKey: "validUntil"
      });

    const initSanctionForm = ref({
      vuelidateValidations: {},
      readOnlyFields: [],
      formFields: []
    });

    const { v$, fieldErrorMessages, fieldDisabled, legacyFormErrorMessages } =
      useFormValidation(initSanctionForm, form);

    return {
      v$,
      fieldErrorMessages,
      fieldDisabled,
      legacyFormErrorMessages,

      form,
      initSanctionForm,
      saveMetaSetOriginalForm,
      saveMetaConfirmUnsaveChanges,

      validFrom,
      validUntil
    };
  },
  data() {
    return {
      formError: {},
      initLoading: true,
      loading: false,
      value: false
    };
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(v) {
        if (v == false) {
          this.saveMetaConfirmUnsaveChanges().then(response => {
            if (!response) {
              return;
            }
            this.initLoading = true;
            this.formError = {};
            this.form = {
              id: null,
              sanctionDefinitionId: null,
              sanctionedPersonId: this.values.sanctionedPersonId,
              sanctionedHorseId: this.values.sanctionedHorseId,
              sanctionedEntity: !this.sanctionedEntity
                ? "Person"
                : this.sanctionedEntity,
              sanctionReasonIds: null,
              validFrom: null,
              validUntil: null,
              relatedEventIds: this.values.relatedEventIds ?? [],
              relatedPersonIds: [],
              relatedHorseIds: [],
              publicComment: null,
              internalComment: null
            };
            this.saveMetaSetOriginalForm(this.form);
            this.value = v;
            this.v$.form.$reset();
          });
        } else {
          this.value = v;
        }
      }
    },
    formFields() {
      return this.initSanctionForm.formFields.reduce((o, field) => {
        o[field.name] = field;
        return o;
      }, {});
    },
    sanctionEntityPickerDisabled() {
      return ["Person", "Horse"].includes(this.sanctionedEntity);
    }
  },
  beforeMount() {
    this.form.sanctionedEntity = !this.sanctionedEntity
      ? "Person"
      : this.sanctionedEntity;
    this.form.sanctionedPersonId = this.values.sanctionedPersonId;
    this.form.sanctionedHorseId = this.values.sanctionedHorseId;
    this.form.relatedEventIds = this.values.relatedEventIds ?? [];
  },
  apollo: {
    initSanctionForm: {
      query: gql`
        query initSanctionForm(
          $id: ID
          $sanctionDefinitionId: ID
          $sanctionedEntity: String!
          $sanctionedHorseId: ID
          $sanctionedPersonId: ID
          $relatedEventIds: [ID!]
        ) {
          initSanctionForm(
            id: $id
            sanctionDefinitionId: $sanctionDefinitionId
            sanctionedEntity: $sanctionedEntity
            sanctionedHorseId: $sanctionedHorseId
            sanctionedPersonId: $sanctionedPersonId
            relatedEventIds: $relatedEventIds
          ) {
            readOnlyFields
            vuelidateValidations
            formFields {
              name
              possibleValues
            }
            sanction {
              id
              sanctionedHorseId
              sanctionedPersonId
              validFrom
              validUntil
              customName
              customPublicDescription
            }
          }
        }
      `,
      skip() {
        return this.dialog == false;
      },
      variables() {
        return {
          id: this.form.id,
          sanctionDefinitionId: this.form.sanctionDefinitionId,
          sanctionedEntity: this.form.sanctionedEntity,
          sanctionedHorseId: this.form.sanctionedHorseId,
          sanctionedPersonId: this.form.sanctionedPersonId,
          relatedEventIds: this.form.relatedEventIds
        };
      },
      manual: true,
      result({ loading, data }) {
        if (loading) return;
        this.initSanctionForm = data.initSanctionForm;
        const { sanction } = data.initSanctionForm;
        this.form = {
          ...this.form,
          sanctionedPersonId:
            sanction?.sanctionedPersonId ?? this.form.sanctionedPersonId,
          sanctionedHorseId:
            sanction?.sanctionedHorseId ?? this.form.sanctionedHorseId,
          validFrom: sanction?.validFrom,
          validUntil: sanction?.validUntil,
          customName: sanction?.customName,
          customPublicDescription: sanction?.customPublicDescription
        };
        if (this.initLoading) {
          this.saveMetaSetOriginalForm(this.form);
        }
        this.initLoading = false;
        this.loading = false;
      }
    }
  },
  fragments: {
    sanctionFields: gql`
      fragment _ on Sanction {
        id
        sanctionDefinitionId
        name
        createdAt
        validFrom
        validUntil
        publicComment
        internalComment
        sanctionedEntity
        sanctionReasonIds
        sanctionReasons @client {
          id
          name
        }
        approved
        approvedAt
        relatedEventIds
        relatedPersonIds
        relatedHorseIds
        sanctionedPersonId
        sanctionedHorseId
        allowedMutations
      }
    `
  },
  methods: {
    openDialog() {
      this.dialog = true;
    },
    setFormFields(form) {
      Object.keys(this.form).forEach(key => {
        this.form[key] = form[key];
      });

      this.dialog = true;
    },
    refetchQuery() {
      if (this.refetchQueryName === "sanctions") return "sanctions";
      if (!this.sanctionedEntity) return; // If prop has no value, skip. unneeded query thats not going to be used

      const id = this.form.sanctionedPersonId || this.form.sanctionedHorseId;
      const query = this.refetchQueryName;
      return {
        query: gql`
          query ${query}($id: ID) {
            ${query}(id: $id) {
              id
              suspended
              sanctions {
                ${this.$options.fragments.sanctionFields}
              }
            }
          }
        `,
        variables: {
          id
        }
      };
    },
    async handleSubmit() {
      this.formError = {};
      await this.v$.form.$touch();
      if (this.v$.form.$invalid) {
        this.formError = this.legacyFormErrorMessages();
        return false;
      }

      this.loading = true;
      if (this.form.id) this.sanctionUpdate();
      else this.sanctionCreate();
    },
    async sanctionCreate() {
      try {
        const refetch = [this.refetchQuery()].filter(Boolean);
        await this.$apollo.mutate({
          mutation: gql`
            mutation sanctionCreate(
              $sanctionDefinitionId: ID!
              $sanctionedEntity: String!
              $sanctionedHorseId: ID
              $sanctionedPersonId: ID
              $relatedEventIds: [ID!]
              $relatedPersonIds: [ID!]
              $relatedHorseIds: [ID!]
              $sanctionReasonIds: [ID!]
              $validFrom: Date
              $validUntil: Date
              $customName: String
              $customPublicDescription: String
              $publicComment: String
              $internalComment: String
            ) {
              sanctionCreate(
                input: {
                  sanctionDefinitionId: $sanctionDefinitionId
                  sanctionedEntity: $sanctionedEntity
                  sanctionedHorseId: $sanctionedHorseId
                  sanctionedPersonId: $sanctionedPersonId
                  relatedEventIds: $relatedEventIds
                  relatedPersonIds: $relatedPersonIds
                  relatedHorseIds: $relatedHorseIds
                  sanctionReasonIds: $sanctionReasonIds
                  validFrom: $validFrom
                  validUntil: $validUntil
                  customName: $customName
                  customPublicDescription: $customPublicDescription
                  publicComment: $publicComment
                  internalComment: $internalComment
                }
              ) {
                sanction {
                  ${this.$options.fragments.sanctionFields}
                }
              }
            }
          `,
          refetchQueries: refetch,
          awaitRefetchQueries: true,
          variables: this.form
        });
        if (this.form.sanctionedPersonId || this.form.sanctionedHorseId) {
          this.$emit("open-sanction-tab");
        }
        this.$root.$snackbar("success");
        this.saveMetaSetOriginalForm(this.form);
        this.dialog = false;
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.loading = false;
      }
    },
    async sanctionUpdate() {
      try {
        const refetch = [this.refetchQuery()].filter(Boolean);
        await this.$apollo.mutate({
          mutation: gql`
            mutation sanctionUpdate(
              $id: ID!
              $sanctionDefinitionId: ID!
              $sanctionedHorseId: ID
              $sanctionedPersonId: ID
              $relatedEventIds: [ID!]
              $relatedPersonIds: [ID!]
              $relatedHorseIds: [ID!]
              $sanctionReasonIds: [ID!]
              $validFrom: Date
              $validUntil: Date
              $customName: String
              $customPublicDescription: String
              $publicComment: String
              $internalComment: String
            ) {
              sanctionUpdate(
                input: {
                  id: $id
                  sanctionDefinitionId: $sanctionDefinitionId
                  sanctionedHorseId: $sanctionedHorseId
                  sanctionedPersonId: $sanctionedPersonId
                  relatedEventIds: $relatedEventIds
                  relatedPersonIds: $relatedPersonIds
                  relatedHorseIds: $relatedHorseIds
                  sanctionReasonIds: $sanctionReasonIds
                  validFrom: $validFrom
                  validUntil: $validUntil
                  customName: $customName
                  customPublicDescription: $customPublicDescription
                  publicComment: $publicComment
                  internalComment: $internalComment
                }
              ) {
                sanction {
                  ${this.$options.fragments.sanctionFields}
                }
              }
            }
          `,
          refetchQueries: refetch,

          variables: this.form
        });

        this.$root.$snackbar("success");
        this.saveMetaSetOriginalForm(this.form);
        this.dialog = false;
      } catch (error) {
        this.$root.$snackbarServerError(error, { level: "error" });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>
